import React, { ChangeEvent } from "react";

import MiniSpinner from "@Components/shared/MiniSpinner";
import { IAssessmentDetails, IAssessmentInstanceDetails } from "@Models/IAssessmentDetails";
import { ITaskSubmissionDetails } from "@Models/ITaskDetails";
import { IColumnData, isDevelopment, useFilter, useSearchParams } from "@Utils";
import { DownloadOutlined, LoadingOutlined } from "@ant-design/icons";
import { useTemplatesMap } from "../queries";
import { IUseAssessmentData } from "../types";
import useAssessmentReport from "./useAssessmentReport";
import useStudentAssessments from "./useStudentAssessments";
import { Tooltip } from "antd";

interface IUseStudentAssessmentsData extends IUseAssessmentData<IAssessmentInstanceDetails> {
    assessmentInstances: IAssessmentInstanceDetails[];
    filterText: string;

    onFilter: (event: ChangeEvent<HTMLInputElement>) => void;
    onRowClick: (assessmentId: IAssessmentDetails["assessmentId"]) => void;
}

const useStudentAssessmentTable = (): IUseStudentAssessmentsData => {
    // ** Search params ** //
    const [searchParams, setSearchParams] = useSearchParams();
    const tab: string = searchParams.get("tab");
    const { filterText, onFilter } = useFilter();

    // ** Queries and Mutations ** //
    const { assessmentInstances, isFetching, isLoading, onReload } = useStudentAssessments();
    const { data: templatesMap, isFetched: isTemplatesMapFetched } = useTemplatesMap();
    const { activeDownloads, prepareReport } = useAssessmentReport();

    // ** Event Handlers ** //
    const handleRowClick = (id: IAssessmentDetails["assessmentId"]) => setSearchParams({ tab, assessment: id });

    // ** Column Data for table ** //
    const columnData: IColumnData<IAssessmentInstanceDetails>[] = [
        { key: "name", align: "left", sorter: (a, b) => a.name.localeCompare(b.name) },
        { key: "description", align: "left" },
        {
            key: "taskDetails",
            align: "center",
            title: "Tasks",
            render: (tasks: ITaskSubmissionDetails[]) => tasks?.length,
        },
        {
            key: "feedbackTemplateId",
            align: "center",
            title: "Template",
            render: (value: string) =>
                isTemplatesMapFetched ? (
                    templatesMap?.get(value)?.name ?? ""
                ) : (
                    <MiniSpinner style={{ background: "none" }} />
                ),
        },
    ];

    const filterAssessments = (unfilteredAssessments: IAssessmentInstanceDetails[]) => {
        if (!filterText) return unfilteredAssessments;
        return unfilteredAssessments.filter(
            (assessment) =>
                assessment.name.toLowerCase().includes(filterText) ||
                assessment.description.toLowerCase().includes(filterText)
        );
    };

    const filteredAssessments = filterAssessments(assessmentInstances);

    // these columns are conditional based on the active tab
    if (tab === "assigned") {
        columnData.push({
            key: "startDate",
            title: "Starts",
            render: "date",
            align: "center",
            sorter: (a, b) => a.startDate.localeCompare(b.startDate),
        });
        columnData.push({
            key: "endDate",
            title: "Deadline",
            render: "date",
            align: "center",
            sorter: (a, b) => a.endDate.localeCompare(b.endDate),
        });
    }

    if (tab === "submitted") {
        columnData.push({
            key: "submissionDate",
            title: "Submitted",
            render: "date",
            align: "center",
            sorter: (a, b) => a.submissionDate.localeCompare(b.submissionDate),
        });
    }

    if (tab === "completed") {
        columnData.push({
            key: "completionDate",
            title: "Completed",
            render: "date",
            align: "center",
            sorter: (a, b) => a.completionDate.localeCompare(b.completionDate),
        });
        isDevelopment() &&
            columnData.push({
                className: "no-padding action-cell",
                key: "download",
                align: "center",
                onCell: () => ({
                    onClick: (event) => event.stopPropagation(),
                }),
                title: "",
                // TODO: add a tooltip here
                render: (_, assessmentInstanceDetails) => {
                    return activeDownloads.has(assessmentInstanceDetails.assessmentInstanceId) ? (
                        <LoadingOutlined disabled />
                    ) : (
                        <Tooltip
                            overlayStyle={{ paddingRight: 0, zIndex: 999 }}
                            placement="left"
                            title="Download assessment report"
                        >
                            <DownloadOutlined onClick={() => prepareReport(assessmentInstanceDetails)} />
                        </Tooltip>
                    );
                },
            });
    }

    // ** Return Data ** //
    return {
        assessmentInstances: filteredAssessments,
        columnData,
        filterText,
        isFetching,
        isLoading,
        onFilter,
        onReload,
        onRowClick: handleRowClick,
    };
};

export default useStudentAssessmentTable;
